<template>
  <div>
    <!-- Mobile Sidebar Overlay -->
    <div v-if="isOpen" class="fixed inset-0 z-50 lg:hidden">
      <div class="fixed inset-0 bg-gray-900/80" @click="$emit('close')" />
      
      <div class="fixed inset-y-0 left-0 w-64 bg-white shadow-xl">
        <div class="flex flex-col h-full">
          <!-- Header -->
          <div class="flex h-16 shrink-0 items-center px-6 border-b">
            <span class="text-lg font-bold text-gray-900 leading-tight">{{ sidebarTitle }}</span>
            <button 
              @click="$emit('close')"
              class="ml-auto p-2 text-gray-600 hover:text-gray-900"
            >
              <XMarkIcon class="h-6 w-6" />
            </button>
          </div>
          
          <!-- Navigation -->
          <nav class="flex-1 p-4 space-y-1">
            <template v-for="item in navigation" :key="item.name">
              <div v-if="item.children" class="space-y-1">
                <button
                  @click="toggleMenu(item.name)"
                  class="flex items-center justify-between w-full px-3 py-2 rounded-md text-sm font-medium"
                  :class="isMenuActive(item) ? 'bg-orange-50 text-orange-600' : 'text-gray-700 hover:text-orange-600 hover:bg-gray-50'"
                >
                  <div class="flex items-center space-x-3">
                    <component :is="item.icon" class="h-6 w-6 shrink-0" />
                    <span>{{ item.name }}</span>
                  </div>
                  <ChevronDownIcon
                    class="h-4 w-4 transition-transform"
                    :class="expandedMenus.includes(item.name) ? 'rotate-180' : ''"
                  />
                </button>
                <div v-if="expandedMenus.includes(item.name)" class="pl-10 space-y-1">
                  <router-link
                    v-for="child in item.children"
                    :key="child.name"
                    :to="child.to"
                    @click="$emit('close')"
                    class="block px-3 py-2 rounded-md text-sm font-medium"
                    :class="$route.path === child.to ? 'bg-orange-50 text-orange-600' : 'text-gray-600 hover:text-orange-600 hover:bg-gray-50'"
                  >
                    {{ child.name }}
                  </router-link>
                </div>
              </div>
              <router-link
                v-else
                :to="item.to"
                @click="$emit('close')"
                class="flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium"
                :class="$route.path === item.to || $route.path.startsWith(item.to + '/') ? 'bg-orange-50 text-orange-600' : 'text-gray-700 hover:text-orange-600 hover:bg-gray-50'"
              >
                <component :is="item.icon" class="h-6 w-6 shrink-0" />
                <span>{{ item.name }}</span>
              </router-link>
            </template>
          </nav>
          
          <!-- Logout -->
          <div class="p-4 border-t">
            <button
              @click="logout"
              class="flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-red-600 w-full"
            >
              <ArrowLeftOnRectangleIcon class="h-6 w-6 shrink-0" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Desktop Sidebar -->
    <div class="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-64 lg:flex-col">
      <div class="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
        <div class="flex h-16 shrink-0 items-center">
          <span class="text-lg font-bold text-gray-900 leading-tight">{{ sidebarTitle }}</span>
        </div>
        <nav class="flex flex-1 flex-col gap-y-1">
          <template v-for="item in navigation" :key="item.name">
            <div v-if="item.children" class="space-y-1">
              <button
                @click="toggleMenu(item.name)"
                class="flex items-center justify-between w-full px-3 py-2 rounded-md text-sm font-medium"
                :class="isMenuActive(item) ? 'bg-orange-50 text-orange-600' : 'text-gray-700 hover:text-orange-600 hover:bg-gray-50'"
              >
                <div class="flex items-center space-x-3">
                  <component :is="item.icon" class="h-6 w-6 shrink-0" />
                  <span>{{ item.name }}</span>
                </div>
                <ChevronDownIcon
                  class="h-4 w-4 transition-transform"
                  :class="expandedMenus.includes(item.name) ? 'rotate-180' : ''"
                />
              </button>
              <div v-if="expandedMenus.includes(item.name)" class="pl-10 space-y-1">
                <router-link
                  v-for="child in item.children"
                  :key="child.name"
                  :to="child.to"
                  class="block px-3 py-2 rounded-md text-sm font-medium"
                  :class="$route.path === child.to ? 'bg-orange-50 text-orange-600' : 'text-gray-600 hover:text-orange-600 hover:bg-gray-50'"
                >
                  {{ child.name }}
                </router-link>
              </div>
            </div>
            <router-link
              v-else
              :to="item.to"
              class="flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium"
              :class="$route.path === item.to || $route.path.startsWith(item.to + '/') ? 'bg-orange-50 text-orange-600' : 'text-gray-700 hover:text-orange-600 hover:bg-gray-50'"
            >
              <component :is="item.icon" class="h-6 w-6 shrink-0" />
              <span>{{ item.name }}</span>
            </router-link>
          </template>
        </nav>
        
        <!-- Logout -->
        <div class="mt-auto pt-4 border-t">
          <button
            @click="logout"
            class="flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-red-600 w-full"
          >
            <ArrowLeftOnRectangleIcon class="h-6 w-6 shrink-0" />
            <span>Sair</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import {
  HomeIcon,
  ShoppingBagIcon,
  CubeIcon,
  TagIcon,
  UsersIcon,
  TruckIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
  XMarkIcon,
  UserGroupIcon,
  ChartPieIcon,
  ChevronDownIcon
} from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores'
import { companyApi } from '@/utils/api'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

const expandedMenus = ref<string[]>([])

// Nome da empresa para o título da sidebar
const companyName = ref('')

const sidebarTitle = computed(() =>
  companyName.value ? `DeliveryAppHub - ${companyName.value}` : 'DeliveryAppHub'
)

onMounted(async () => {
  if (authStore.companyId) {
    try {
      const res = await companyApi.getById(authStore.companyId)
      companyName.value = res.data.name || ''
    } catch (error) {
      console.error('Erro ao carregar nome da empresa:', error)
    }
  }
})

const isMenuActive = (item: any) => {
  if (!item.children) return false
  return item.children.some((child: any) => route.path === child.to || route.path.startsWith(child.to + '/'))
}

const toggleMenu = (name: string) => {
  if (expandedMenus.value.includes(name)) {
    expandedMenus.value = expandedMenus.value.filter(n => n !== name)
  } else {
    expandedMenus.value.push(name)
  }
}

defineProps<{
  isOpen: boolean
}>()

defineEmits<{
  close: []
}>()

const navigation = computed(() => [
  { name: 'Dashboard', to: '/admin', icon: HomeIcon },
  { name: 'Pedidos', to: '/admin/orders', icon: ShoppingBagIcon },
  { name: 'Produtos', to: '/admin/products', icon: CubeIcon },
  { name: 'Categorias', to: '/admin/categories', icon: TagIcon },
  { name: 'Clientes', to: '/admin/customers', icon: UsersIcon },
  { name: 'Entrega', to: '/admin/delivery', icon: TruckIcon },
  { name: 'Entregadores', to: '/admin/drivers', icon: UserGroupIcon },
  {
    name: 'Relatórios',
    icon: ChartPieIcon,
    children: [
      { name: 'Vendas', to: '/admin/reports/sales' },
      { name: 'Entregas', to: '/admin/reports/deliveries' }
    ]
  },
  { name: 'Configurações', to: '/admin/settings', icon: Cog6ToothIcon },
])

const logout = () => {
  authStore.logout()
  router.push('/login')
}
</script>
